import styled from 'styled-components';
import { zIndex } from 'tokens';

interface SidebarProps {
	isMobile: boolean;
	sidebarVisible: boolean;
	sidebarWidth: number;
}

export const Base = styled.div<SidebarProps>`
	display: flex;
	flex-direction: column;
	overflow: hidden;
	height: 100vh; /* fallback for older browsers */
	height: 100svh; /* small viewport height - excludes dynamic UI */
	background-color: ${({ theme }) => theme.white};
	border-right: 1px solid ${({ theme }) => theme.gray['200']};

	${({ isMobile, sidebarVisible, sidebarWidth }) =>
		isMobile
			? `
		position: fixed;
		top: 0;
		left: 0;
		width: ${sidebarWidth}px;
		z-index: 1000;
		transform: translateX(${sidebarVisible ? '0' : '-100%'});
		transition: transform 0.3s ease;
		box-shadow: 4px 0 12px rgba(0, 0, 0, 0.1);
	`
			: `
		grid-column: 1;
		z-index: ${zIndex.panel};
	`}
`;

export const Header = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 24px 20px 16px 20px;
	flex-shrink: 0;
	gap: 12px;
	white-space: nowrap;
	border-bottom: 1px solid ${({ theme }) => theme.gray['200']};
`;

export const LogoIconWrapper = styled.div`
	display: flex;
	align-items: center;
	filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.2));
	transition: filter 0.3s ease;
`;

export const HeaderContent = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	cursor: pointer;
	flex: 1;
	transition: transform 0.2s ease;

	&:hover {
		transform: translateX(2px);
	}

	&:hover ${LogoIconWrapper} {
		filter: drop-shadow(0 0 12px rgba(255, 255, 255, 0.4));
	}
`;

export const CloseButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0;
	width: 32px;
	height: 32px;
	border: none;
	background: transparent;
	cursor: pointer;
	border-radius: 6px;
	transition: background-color 0.2s ease;

	&:hover {
		background-color: ${({ theme }) => theme.gray['100']};
	}

	&:active {
		background-color: ${({ theme }) => theme.gray['200']};
	}
`;

export const Moonchild = styled.div`
	color: ${({ theme }) => theme.gray['1000']};
	opacity: 0.95;

	& svg {
		height: 18px;
		fill: currentColor;
		display: block;
	}
	padding-bottom: 2px;
`;

export const NewProjectButton = styled.button`
	background: linear-gradient(135deg, #1e90ff 0%, #4169e1 100%);
	color: white;
	border: none;
	padding: 12px 20px;
	font-weight: 600;
	font-size: 15px;
	cursor: pointer;
	flex-shrink: 0;
	margin: 20px 16px;
	border-radius: 8px;
	white-space: nowrap;
	box-shadow: 0 4px 12px rgba(30, 144, 255, 0.25), 0 2px 4px rgba(0, 0, 0, 0.3);
	transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
	position: relative;
	overflow: hidden;

	&::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%);
		opacity: 0;
		transition: opacity 0.2s ease;
	}

	&:hover:not([disabled]) {
		transform: translateY(-2px);
		box-shadow: 0 6px 16px rgba(30, 144, 255, 0.35), 0 4px 8px rgba(0, 0, 0, 0.4);
	}

	&:hover:not([disabled])::before {
		opacity: 1;
	}

	&:active:not([disabled]) {
		transform: translateY(-1px);
		box-shadow: 0 3px 10px rgba(30, 144, 255, 0.3), 0 2px 4px rgba(0, 0, 0, 0.3);
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
		transform: none;
		box-shadow: none;
	}
`;

export const Content = styled.div`
	flex: 1;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	min-height: 0;
	overflow-y: auto;
	overflow-x: hidden;
`;

export const SectionHeader = styled.div`
	font-size: 11px;
	font-weight: 800;
	color: ${({ theme }) => theme.gray['400']};
	text-transform: uppercase;
	letter-spacing: 0.06em;
	margin: 20px 12px 16px 18px;
	white-space: nowrap;
	display: flex;
	align-items: center;
	gap: 8px;

	&::after {
		content: '';
		width: 100%;
		height: 1px;
		background: linear-gradient(-90deg, transparent 0%, rgba(156, 163, 175, 0.2) 100%);
		margin-right: 8px;
	}
`;

export const ProjectsList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	margin-bottom: 20px;
	padding: 0 12px;
`;

export const LoadingWrapper = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 40px 20px;
`;

export const Footer = styled.div`
	padding: 10px 12px;
	padding-top: 16px;
	border-top: 1px solid ${({ theme }) => theme.gray['200']};
	display: flex;
	flex-direction: column;
	flex-shrink: 0;
	background-color: ${({ theme }) => theme.white};
`;

export const ChromePluginButton = styled.button`
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 10px 16px;
	background: ${({ theme }) => theme.gray['100']};
	border: none;
	border-radius: 6px;
	color: ${({ theme }) => theme.gray['1000']};
	font-weight: 600;
	font-size: 13px;
	cursor: pointer;
	transition: background 0.2s ease;
	margin-bottom: 4px;
	white-space: nowrap;
	overflow: hidden;

	&:hover {
		background: ${({ theme }) => theme.gray['200']};
	}
`;

export const ProfileSettingsRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 4px 0;
`;

export const ProfileButton = styled.button`
	flex: 1;
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 6px 8px;
	background: transparent;
	border: none;
	border-radius: 6px;
	color: ${({ theme }) => theme.gray['1000']};
	font-size: 13px;
	cursor: pointer;
	transition: background 0.2s ease;
	min-width: 0;

	&:hover {
		background-color: ${({ theme }) => theme.gray['200']};
	}
`;

export const ProfileName = styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-weight: 500;
`;

export const SettingsButton = styled.button`
	width: 40px;
	height: 44px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: transparent;
	border: none;
	border-radius: 6px;
	color: ${({ theme }) => theme.gray['500']};
	cursor: pointer;
	transition: all 0.2s ease;
	flex-shrink: 0;

	&:hover {
		background-color: ${({ theme }) => theme.gray['200']};
		color: ${({ theme }) => theme.gray['1000']};
	}
`;

export const Overlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vh;
	background: rgba(0, 0, 0, 0.4);
	z-index: 999;
	cursor: pointer;
	backdrop-filter: blur(2px);
	transition: opacity 0.3s ease;
`;
